import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarDays,
  MapPin,
  Dumbbell,
  Gauge,
  Settings,
  SlidersHorizontal,
  Sparkles,
  ScrollText,
  Backpack,
  Share2,
  Building2,
  UserPlus,
  UserCog,
  LogOut,
  User,
  Users,
  Clock,
  CalendarClock,
  GraduationCap,
  Trophy,
  HelpCircle,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  ChevronRight,
  Star,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@sportscanner/ui";
import { hasFeature } from "@sportscanner/schemas";
import { signOut } from "@/lib/auth-client";
import { useMe } from "@/lib/me";
import LanguageSwitcher from "./language-switcher";

const COLLAPSE_STORAGE_KEY = "admin_sidebar_collapsed";
const GROUPS_STORAGE_KEY = "admin_sidebar_groups";

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
}

function NavItemLink({
  to,
  icon,
  label,
  collapsed,
}: NavItem & { collapsed: boolean }) {
  const link = (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        collapsed
          ? `mx-auto flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
              isActive
                ? "bg-primary-50 text-primary-700"
                : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800"
            }`
          : `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? "bg-primary-50 text-primary-700"
                : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800"
            }`
      }
    >
      <span className="w-4 h-4 shrink-0">{icon}</span>
      {!collapsed && label}
    </NavLink>
  );

  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

// A collapsible nav section. Only rendered in the expanded sidebar — the
// icon-only rail falls back to a flat divider-separated list (see SidebarBody).
function NavGroup({
  label,
  items,
  open,
  onToggle,
}: {
  label: string;
  items: NavItem[];
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 pt-3 pb-1 text-xs font-semibold text-neutral-400 uppercase tracking-wider hover:text-neutral-600 transition-colors cursor-pointer"
      >
        <span className="flex-1 min-w-0 truncate text-left">{label}</span>
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 shrink-0" />
        )}
      </button>
      {open && (
        <div className="space-y-1">
          {items.map((item) => (
            <NavItemLink key={item.to} collapsed={false} {...item} />
          ))}
        </div>
      )}
    </div>
  );
}

interface SidebarBodyProps {
  collapsed: boolean;
  onToggleCollapsed?: () => void;
}

function SidebarBody({ collapsed, onToggleCollapsed }: SidebarBodyProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, club } = useMe();
  const role = user?.role || "STAFF";

  // Which accordion groups are expanded. A group id absent from the map reads as
  // collapsed (`?? false`), so groups start closed — the active route's group is
  // auto-opened below. Choices persist per browser.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    if (typeof window === "undefined") return {};
    try {
      return JSON.parse(
        window.localStorage.getItem(GROUPS_STORAGE_KEY) || "{}",
      ) as Record<string, boolean>;
    } catch {
      return {};
    }
  });
  useEffect(() => {
    window.localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  // Time-slot editing is available to any active-subscription MANAGER and
  // every SUPERADMIN. The page itself surfaces member-only / prime checkboxes
  // only when the corresponding club feature is on, but custom durations,
  // maintenance closures, and price overrides are tier-agnostic.
  const showTimeSlots = role === "SUPERADMIN" || role === "MANAGER";
  // The members page only makes sense when the club actually has a membership
  // concept — i.e. it requires membership to book, or gates specific slots to
  // members. Superadmin always sees it (they pick the club inside the page).
  const showMembers =
    role === "SUPERADMIN" ||
    (role === "MANAGER" &&
      (!!club?.requireMembership || !!club?.allowMemberOnlySlots));
  // Coach management is club-scoped, so it's a MANAGER tool (superadmin invites
  // coaches via the club picker on the Invite page). The enable toggle now lives
  // on the Coaches page itself, so the nav is gated on the club's TIER rather
  // than the allowCoaching flag — otherwise the page would be hidden until
  // coaching is already on, making it impossible to turn on the first time.
  const showCoaches =
    role === "MANAGER" &&
    !!club &&
    hasFeature(club.subscriptionTier, "allowCoaching");
  // Always reachable for managers — the Reviews page is where ratings get
  // switched on, so it can't hide behind the very flag it toggles.
  const showReviews = role === "MANAGER" || role === "SUPERADMIN";
  // Competitions: a PRO feature. Like Coaches, the nav is gated on TIER (not the
  // enable flag) so the page — where the flag is switched on — is reachable to
  // turn it on the first time. Superadmin manages any club via the picker.
  const showCompetitions =
    role === "SUPERADMIN" ||
    (role === "MANAGER" &&
      !!club &&
      hasFeature(club.subscriptionTier, "competitionsEnabled"));
  const displayName = user
    ? `${user.firstName} ${user.lastName}`.trim() || user.email
    : "";

  // ── Nav model ──────────────────────────────────────────────────────────────
  // Flat operational items shown above the grouped setup/config sections.
  const topItems: NavItem[] = [];
  if (role === "COACH") {
    topItems.push({
      to: "/coaching",
      icon: <CalendarClock className="w-4 h-4" />,
      label: t("nav.mySchedule"),
    });
  } else {
    topItems.push({
      to: "/",
      icon: <LayoutDashboard className="w-4 h-4" />,
      label: t("nav.home"),
    });
  }
  if (role === "MANAGER" || role === "SUPERADMIN" || role === "STAFF") {
    topItems.push({
      to: "/bookings",
      icon: <CalendarDays className="w-4 h-4" />,
      label: t("nav.bookings"),
    });
  }
  if (showMembers) {
    topItems.push({
      to: "/players",
      icon: <Users className="w-4 h-4" />,
      label: t("nav.members"),
    });
  }
  if (showReviews) {
    topItems.push({
      to: "/reviews",
      icon: <Star className="w-4 h-4" />,
      label: t("nav.reviews"),
    });
  }
  if (showCompetitions) {
    topItems.push({
      to: "/competitions",
      icon: <Trophy className="w-4 h-4" />,
      label: t("nav.competitions"),
    });
  }

  // Collapsible groups (MANAGER / SUPERADMIN only). Items keep their own
  // visibility, and a group with no visible items is dropped below.
  const groups: { id: string; label: string; items: NavItem[] }[] = [];
  if (role === "MANAGER" || role === "SUPERADMIN") {
    groups.push({
      id: "bookingSetup",
      label: t("nav.groups.bookingSetup"),
      items: [
        {
          to: "/sports",
          icon: <Dumbbell className="w-4 h-4" />,
          label: t("nav.sports"),
        },
        {
          to: "/courts",
          icon: <MapPin className="w-4 h-4" />,
          label: t("nav.courts"),
        },
        ...(showTimeSlots
          ? [
              {
                to: "/time-slots",
                icon: <Clock className="w-4 h-4" />,
                label: t("nav.timeSlots"),
              },
            ]
          : []),
      ],
    });
    groups.push({
      id: "offerings",
      label: t("nav.groups.offerings"),
      items: [
        {
          to: "/gear",
          icon: <Backpack className="w-4 h-4" />,
          label: t("nav.gear"),
        },
        ...(showCoaches
          ? [
              {
                to: "/coaches",
                icon: <GraduationCap className="w-4 h-4" />,
                label: t("nav.coaches"),
              },
            ]
          : []),
      ],
    });
    groups.push({
      id: "configuration",
      label: t("nav.groups.configuration"),
      items: [
        {
          to: "/settings",
          icon: <Settings className="w-4 h-4" />,
          label: t("nav.settings"),
        },
        {
          to: "/limits",
          icon: <Gauge className="w-4 h-4" />,
          label: t("nav.limits"),
        },
        {
          to: "/access",
          icon: <SlidersHorizontal className="w-4 h-4" />,
          label: t("nav.access"),
        },
        {
          to: "/club-rules",
          icon: <ScrollText className="w-4 h-4" />,
          label: t("nav.clubRules"),
        },
        {
          to: "/amenities",
          icon: <Sparkles className="w-4 h-4" />,
          label: t("nav.amenities"),
        },
      ],
    });
    if (role === "SUPERADMIN") {
      groups.push({
        id: "superadmin",
        label: t("nav.superadminSection"),
        items: [
          {
            to: "/clubs",
            icon: <Building2 className="w-4 h-4" />,
            label: t("nav.clubs"),
          },
          {
            to: "/admins",
            icon: <UserCog className="w-4 h-4" />,
            label: t("nav.admins"),
          },
          {
            to: "/invite",
            icon: <UserPlus className="w-4 h-4" />,
            label: t("nav.invite"),
          },
        ],
      });
    } else {
      groups.push({
        id: "team",
        label: t("nav.groups.team"),
        items: [
          {
            to: "/admins",
            icon: <UserCog className="w-4 h-4" />,
            label: t("nav.admins"),
          },
          {
            to: "/invite",
            icon: <UserPlus className="w-4 h-4" />,
            label: t("nav.invite"),
          },
        ],
      });
    }
  }

  // Flat tail items below the groups. Integration sits immediately above Help.
  const tailItems: NavItem[] = [];
  if (role === "MANAGER" || role === "SUPERADMIN") {
    tailItems.push({
      to: "/integration",
      icon: <Share2 className="w-4 h-4" />,
      label: t("nav.integration"),
    });
  }
  tailItems.push({
    to: "/help",
    icon: <HelpCircle className="w-4 h-4" />,
    label: t("nav.help"),
  });

  // Keep the group that owns the active route open so deep links never land on a
  // hidden item. Fires only when the active group changes, so a manual collapse
  // of the current group is respected until the user navigates elsewhere.
  const matchActive = (to: string) =>
    location.pathname === to || location.pathname.startsWith(`${to}/`);
  const activeGroupId =
    groups.find((g) => g.items.some((it) => matchActive(it.to)))?.id ?? null;
  useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((prev) =>
      prev[activeGroupId] ? prev : { ...prev, [activeGroupId]: true },
    );
  }, [activeGroupId]);

  async function handleLogout() {
    await signOut({ fetchOptions: { onSuccess: () => navigate("/login") } });
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="h-full flex flex-col bg-white">
        {/* Logo */}
        <div
          className={`border-b border-neutral-200 ${collapsed ? "px-2 py-4 flex items-center justify-center" : "px-4 py-5"}`}
        >
          {collapsed ? (
            <span
              className="font-heading text-xl font-bold text-primary-600"
              aria-label="SportScanner"
            >
              S
            </span>
          ) : (
            <>
              <div className="flex items-center gap-1">
                <span className="font-heading text-lg font-bold text-primary-600">
                  Sport
                </span>
                <span className="font-heading text-lg font-bold text-neutral-800">
                  Scanner
                </span>
              </div>
              <p className="text-xs text-neutral-400 mt-0.5">
                {t("nav.adminPanel")}
              </p>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav
          className={`flex-1 ${collapsed ? "px-2" : "px-3"} py-4 space-y-1 overflow-y-auto overflow-x-hidden`}
        >
          {topItems.map((item) => (
            <NavItemLink key={item.to} collapsed={collapsed} {...item} />
          ))}

          {groups.map((g) =>
            collapsed ? (
              // Icon-only rail: no room for headers, so flatten the group to its
              // icons behind a divider (matches the old superadmin-section rule).
              <div key={g.id}>
                <div className="my-2 mx-2 border-t border-neutral-200" />
                {g.items.map((item) => (
                  <NavItemLink key={item.to} collapsed {...item} />
                ))}
              </div>
            ) : (
              <NavGroup
                key={g.id}
                label={g.label}
                items={g.items}
                open={openGroups[g.id] ?? false}
                onToggle={() =>
                  setOpenGroups((prev) => ({
                    ...prev,
                    [g.id]: !(prev[g.id] ?? false),
                  }))
                }
              />
            ),
          )}

          {/* Integration + Help, set off from the groups above by a divider. */}
          {collapsed ? (
            <div className="my-2 mx-2 border-t border-neutral-200" />
          ) : (
            groups.length > 0 && (
              <div className="my-2 mx-3 border-t border-neutral-200" />
            )
          )}
          {tailItems.map((item) => (
            <NavItemLink key={item.to} collapsed={collapsed} {...item} />
          ))}
        </nav>

        {/* Collapse toggle (desktop only) */}
        {onToggleCollapsed && (
          <div className={collapsed ? "px-2 pb-2" : "px-3 pb-2"}>
            <button
              type="button"
              onClick={onToggleCollapsed}
              className={
                collapsed
                  ? "mx-auto flex items-center justify-center w-10 h-10 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors cursor-pointer"
                  : "w-full inline-flex items-center justify-end gap-2 px-2 py-1.5 rounded-lg text-xs text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors cursor-pointer"
              }
              aria-label={
                collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")
              }
              title={
                collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")
              }
            >
              {collapsed ? (
                <ChevronsRight className="w-4 h-4" />
              ) : (
                <ChevronsLeft className="w-4 h-4" />
              )}
            </button>
          </div>
        )}

        {/* User info + logout */}
        <div
          className={`border-t border-neutral-200 ${collapsed ? "p-2" : "p-3"}`}
        >
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to="/account"
                  className={({ isActive }) =>
                    `mx-auto flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                      isActive ? "bg-primary-50" : "hover:bg-neutral-100"
                    }`
                  }
                >
                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-primary-600" />
                  </div>
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right">{displayName}</TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to="/account"
              title={t("nav.myAccount")}
              className={({ isActive }) =>
                `flex items-center gap-3 px-2 py-2 rounded-lg transition-colors group ${
                  isActive ? "bg-primary-50" : "hover:bg-neutral-100"
                }`
              }
            >
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                <User className="w-4 h-4 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-800 truncate">
                  {displayName}
                </p>
                <p className="text-xs text-neutral-400">{t(`roles.${role}`)}</p>
              </div>
            </NavLink>
          )}
          {!collapsed && (
            <div className="mt-1 flex items-center justify-between px-2 py-2">
              <span className="text-sm text-neutral-500">
                {t("nav.language")}
              </span>
              <LanguageSwitcher />
            </div>
          )}
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleLogout}
                  className="mx-auto flex cursor-pointer items-center justify-center w-10 h-10 mt-1 rounded-lg text-sm text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-colors"
                  aria-label={t("nav.logout")}
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{t("nav.logout")}</TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full flex cursor-pointer items-center gap-3 px-2 py-2 rounded-lg text-sm text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              {t("nav.logout")}
            </button>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

interface SidebarProps {
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
}

export default function Sidebar({
  mobileOpen,
  onMobileOpenChange,
}: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === "1";
  });

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // Auto-close mobile drawer when route changes.
  useEffect(() => {
    onMobileOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex ${collapsed ? "w-16" : "w-60"} shrink-0 sticky top-0 h-svh border-r border-neutral-200 flex-col bg-white transition-[width] duration-200`}
      >
        <SidebarBody
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((c) => !c)}
        />
      </aside>

      {/* Mobile drawer (always expanded) */}
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent
          side="left"
          className="p-0 w-72 max-w-[85vw] sm:max-w-sm gap-0"
        >
          <SheetTitle className="sr-only">{t("nav.adminPanel")}</SheetTitle>
          <SidebarBody collapsed={false} />
        </SheetContent>
      </Sheet>
    </>
  );
}
